import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Detect if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle contact actions based on device type
  const handleContactAction = (type) => {
    if (isMobile) {
      // On mobile, use native apps
      if (type === 'phone') {
        window.location.href = 'tel:+919904449922';
      } else if (type === 'email') {
        window.location.href = 'mailto:nitumf2017@gmail.com';
      }
    } else {
      // On desktop, scroll to contact form
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleNavigation = (sectionId) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If we're on home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <footer className="footer">
      <div className="container">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-6 cursor-pointer" onClick={handleLogoClick}>
                <img 
                  src="/nsa-logo.png" 
                  alt="NSA Financial Services" 
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <h3 className="text-xl font-poppins font-semibold text-white">
                    NSA Financial Services
                  </h3>
                  <p className="text-primary-gold text-sm">
                    Your Partner in Financial Growth & Assurance
                  </p>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                Dedicated to helping clients navigate market volatility and achieve long-term financial goals through personalized, trustworthy guidance and proven expertise.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-primary-gold" />
                  <button 
                    onClick={() => handleContactAction('phone')}
                    className="text-gray-300 hover:text-primary-gold transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                  >
                    +91 99044 49922
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-primary-gold" />
                  <button 
                    onClick={() => handleContactAction('email')}
                    className="text-gray-300 hover:text-primary-gold transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                  >
                    nitumf2017@gmail.com
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={16} className="text-primary-gold" />
                  <span className="text-gray-300">426- shivalik shilp iscon char cross road  , Ahmedabad - 380015</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-poppins font-semibold text-white mb-6">
                Quick Links
              </h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => handleNavigation('home')} 
                    className="text-gray-300 hover:text-primary-gold transition-colors"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('about')} 
                    className="text-gray-300 hover:text-primary-gold transition-colors"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('services')} 
                    className="text-gray-300 hover:text-primary-gold transition-colors"
                  >
                    Our Services
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('contact')} 
                    className="text-gray-300 hover:text-primary-gold transition-colors"
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-poppins font-semibold text-white mb-6">
                Our Services
              </h4>
              <ul className="space-y-3">
                <li>
                  <span className="text-gray-300">Mutual Fund Investing</span>
                </li>
                <li>
                  <span className="text-gray-300">Fixed Deposits & Secure Savings</span>
                </li>
                <li>
                  <span className="text-gray-300">Financial Planning & Wealth Guidance</span>
                </li>
                <li>
                  <span className="text-gray-300">Insurance Services</span>
                </li>
              </ul>
              
              {/* Call to Action */}
              <div className="mt-8 p-4 bg-primary-gold bg-opacity-20 rounded-xl border border-primary-gold border-opacity-30">
                <h5 className="font-poppins font-semibold text-white mb-2">
                  Ready to Start?
                </h5>
                <p className="font-poppins font-small text-gray mb-1">
                  Schedule your free consultation today.
                </p>
                <button 
                  onClick={() => handleNavigation('contact')}
                  className="font-poppins font-medium hover:text-gray-300 transition-colors text-sm"
                >
                  Get Started →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-gray-600 py-8">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <div className="text-gray-400 text-sm text-center">
              <p>© 2025 NSA Financial Services. All rights reserved.</p>
              <p className="mt-1">Content inspired by best practices in trusted financial advisories.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;